import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { Check, Ban, Undo2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { formatDateTime } from '../../utils/Utils';
import { getSsoDomainApprovals, approveSsoDomain, rejectSsoDomain, revokeSsoDomain } from '../../services/ssoService';
import { Badge, DOMAIN_STATUS, DOMAIN_STATUS_COLORS } from '../sso-settings/config';

// Alan adı sahipliği onayları — YALNIZ superAdmin (spec §10.2). ProtectedRoute `privileges={['superAdmin']}` kurumsal
// yöneticiyi de geçirir (isEnterpriseAdmin kısayolu); bu yüzden sayfa kendi superAdmin denetimini yapar.
// "Kanıt": o kiracıda bu alan adıyla kayıtlı aktif çalışan e-postası sayısı.
const Container = styled.div`display: flex; flex-direction: column; min-height: 0;`;

export default function SsoDomainApprovalsPage() {
  const { t, i18n } = useTranslation();
  const { isSuperAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [approving, setApproving] = useState(null);
  const [noteFor, setNoteFor] = useState(null); // { row, kind: 'reject' | 'revoke' }

  useEffect(() => {
    if (!isSuperAdmin) return;
    setFetching(true);
    getSsoDomainApprovals()
      .then((res) => {
        const ok = res?.data?.status;
        setFailed(!ok);
        const data = ok ? res.data.data || [] : [];
        // Bekleyenler üstte
        setRows([...data].sort((a, b) => (a.status === DOMAIN_STATUS.PENDING ? -1 : 0) - (b.status === DOMAIN_STATUS.PENDING ? -1 : 0)));
      })
      .finally(() => setFetching(false));
  }, [isSuperAdmin, trigger]);

  const reload = useCallback(() => setTrigger((x) => x + 1), []);

  const actions = useMemo(() => [
    { type: 'approve', label: t('sso.domains.approve'), icon: Check, onClick: (r) => setApproving(r), condition: (r) => r.status === DOMAIN_STATUS.PENDING },
    { type: 'reject', label: t('sso.domains.reject'), icon: Ban, onClick: (r) => setNoteFor({ row: r, kind: 'reject' }), condition: (r) => r.status === DOMAIN_STATUS.PENDING },
    { type: 'revoke', label: t('sso.domains.revoke'), icon: Undo2, onClick: (r) => setNoteFor({ row: r, kind: 'revoke' }), condition: (r) => r.status === DOMAIN_STATUS.APPROVED },
  ], [t]);

  const statusNames = [DOMAIN_STATUS.PENDING, DOMAIN_STATUS.APPROVED, DOMAIN_STATUS.REJECTED].map(String);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { display: 'excluded', filter: false } },
    { name: 'tenantName', label: t('sso.domains.columns.tenant'), options: { filter: false } },
    { name: 'domain', label: t('sso.domains.columns.domain'), options: { filter: false } },
    { name: 'connectionName', label: t('sso.domains.columns.connection'), options: { filter: false } },
    { name: 'requestedByName', label: t('sso.domains.columns.requestedBy'), options: { filter: false } },
    { name: 'requestedAt', label: t('sso.domains.columns.requestedAt'), options: { filter: false, customBodyRender: (v) => formatDateTime(v, i18n.language) } },
    { name: 'evidence', label: t('sso.domains.columns.evidence'), options: { filter: false, customBodyRender: (v) => t('sso.domains.evidence', { count: v }) } },
    {
      name: 'status', label: t('sso.domains.columns.status'),
      options: {
        filter: true,
        filterType: 'dropdown',
        filterOptions: { names: statusNames, renderValue: (v) => t(`sso.domainStatus.${v}`) },
        customFilterListOptions: { render: (v) => t(`sso.domainStatus.${v}`) },
        customBodyRender: (v, meta) => (
          <Badge $bg={DOMAIN_STATUS_COLORS[v].bg} $color={DOMAIN_STATUS_COLORS[v].color} title={rows[meta.rowIndex]?.decisionNote || ''}>
            {t(`sso.domainStatus.${v}`)}
          </Badge>
        ),
      },
    },
    {
      name: 'actions', label: ' ',
      options: { filter: false, sort: false, download: false, print: false, customBodyRender: (_, meta) => <OptionsMenu actions={actions} row={rows[meta.rowIndex]} /> },
    },
  ], [t, i18n.language, rows, actions, statusNames]);

  const handleNote = useCallback(async (fd) => {
    try {
      if (noteFor.kind === 'reject') await rejectSsoDomain(noteFor.row.id, fd.note);
      else await revokeSsoDomain(noteFor.row.id, fd.note);
      setNoteFor(null);
      reload();
      return true;
    } catch { return false; }
  }, [noteFor, reload]);

  if (!isSuperAdmin) return <Redirect to="/" />;

  return (
    <Container>
      {failed && <LoadErrorBanner message={t('sso.domains.loadFailed')} />}
      <DataTable
        title={t('sso.domains.title')}
        fetching={fetching}
        columns={columns}
        data={rows}
        serverSide={false}
        tableBodyMaxHeight="calc(100vh - 300px)"
      />
      <ConfirmDialog
        isOpen={Boolean(approving)}
        title={t('sso.domains.approveTitle')}
        message={t('sso.domains.approveMessage', { domain: approving?.domain || '', tenant: approving?.tenantName || '' })}
        onConfirm={async () => { try { await approveSsoDomain(approving.id); } catch { /* toast interceptor'da */ } setApproving(null); reload(); }}
        onCancel={() => setApproving(null)}
      />
      <GenericFormDrawer
        open={Boolean(noteFor)}
        onClose={() => setNoteFor(null)}
        title={noteFor?.kind === 'revoke' ? t('sso.domains.revokeTitle') : t('sso.domains.rejectTitle')}
        schema={[
          { name: 'info', type: 'notice', grid: 12, tone: noteFor?.kind === 'revoke' ? 'warning' : undefined,
            content: noteFor?.kind === 'revoke' ? t('sso.domains.revokeWarning', { domain: noteFor?.row?.domain || '' }) : (noteFor?.row?.domain || '') },
          { name: 'note', label: t('sso.domains.note'), type: 'textarea', rows: 3, grid: 12, required: true, maxLength: 500 },
        ]}
        onSave={handleNote}
      />
    </Container>
  );
}
